import React from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

/**
 * Reminder banner shown when the intern is approved but their Stripe Connect
 * account isn't ready to receive payouts. Hidden once payouts are enabled.
 *
 * Props:
 *   payoutsEnabled       — bool (from stripe_payouts_enabled on dashboard payload)
 *   graceDaysRemaining   — number|null (days left before Stripe becomes a hard gate)
 *   requirementsDue      — string[] (from stripe_requirements_due)
 *   sx                   — optional MUI sx overrides
 */
export default function StripeConnectBanner({
  payoutsEnabled,
  graceDaysRemaining,
  requirementsDue = [],
  sx = {},
}) {
  const navigate = useNavigate();

  if (payoutsEnabled) return null;

  const inGrace = graceDaysRemaining != null && graceDaysRemaining > 0;
  const needsMoreInfo = Array.isArray(requirementsDue) && requirementsDue.length > 0;

  let severity = 'warning';
  let title = 'Complete your payment setup';
  let message = "You need to finish Stripe onboarding before you can accept jobs or earn referral bonuses.";

  if (needsMoreInfo) {
    title = 'Stripe needs more information';
    message = "Stripe has flagged your account for additional details. Open Payment Setup to continue.";
  } else if (inGrace) {
    title = 'Set up payouts to keep accepting jobs';
    message = `You have ${graceDaysRemaining} day${graceDaysRemaining === 1 ? '' : 's'} of grace remaining before payouts become required to accept new bookings.`;
  } else {
    severity = 'error';
    title = 'Payment setup required';
    message = "Your grace period has ended. Complete Stripe onboarding to resume accepting jobs.";
  }

  return (
    <Alert
      severity={severity}
      icon={<AccountBalanceWalletIcon fontSize="inherit" />}
      sx={{ mb: 3, alignItems: 'center', ...sx }}
      action={
        <Button
          color="inherit"
          size="small"
          variant="outlined"
          onClick={() => navigate('/payouts/setup')}
        >
          Set up payouts
        </Button>
      }
    >
      <AlertTitle sx={{ mb: 0.5 }}>{title}</AlertTitle>
      {message}
    </Alert>
  );
}
